import React from "react";

import { SelectList } from "@common/selectList";
import { Props, Views } from "./types";
import { Search } from "./search";
import { Zones } from "./zones";
import { Projects } from "./projects";
import { ProjectsElementsTags } from "./projectsElementsTags";
import { ProjectsTags } from "./projectsTags";
import { ProjectStatuses } from "./projectStatuses";
import { ProjectPortfolios } from "./projectPortfolios";
import { Resources } from "./resources";
import { ResourceGroups } from "./resourceGroups";
import { ResourceTags } from "./resourceTags";
import { Roles } from "./roles";
import { OrganizationalUnits } from "./organizationalUnits";
import { EraseButton } from "./eraseButton";
import { Input } from "./input";
import styles from "./styles.scss";

function FilterMenu({
  filter,
  views,
  placeholder = "",
  openedViews = [],
  roleScope,
  setFilter,
  erase,
}: Props) {
  const [search, setSearch] = React.useState("");
  const isActivated = (view: Views) => !!views.find((v) => v === view);
  const isViewOpen = (view: Views) => !!openedViews.find((v) => v === view);
  const hasOtherResourceCriteria = (
    ["groups", "resourceTags", "roles", "organizationalUnits"] as const
  ).some((view) => isActivated(view) && (filter[view]?.length ?? 0) > 0);

  return (
    <SelectList rootClassName={styles.root} listClassName={styles.list}>
      {({ isOpen, select }) => ({
        input: (
          <Input
            placeholder={placeholder}
            filter={filter}
            views={views}
            isOpen={isOpen}
          />
        ),
        list: (
          <div className={styles.filterMenu}>
            <div className={styles.search}>
              <Search search={search} setSearch={setSearch} />
            </div>
            {isActivated("zones") && (
              <Zones
                search={search}
                filter={filter.projects}
                setFilter={(projects) => setFilter({ ...filter, projects })}
                isOpen={isViewOpen("zones")}
              />
            )}
            {isActivated("projects") && (
              <Projects
                search={search}
                filter={filter.projects}
                setFilter={(projects) => setFilter({ ...filter, projects })}
                isOpen={isViewOpen("projects")}
              />
            )}
            {isActivated("projectPortfolios") && (
              <ProjectPortfolios
                search={search}
                filter={filter.projectPortfolios}
                setFilter={(projectPortfolios) =>
                  setFilter({ ...filter, projectPortfolios })
                }
                isOpen={isViewOpen("projectPortfolios")}
              />
            )}
            {isActivated("resources") && (
              <Resources
                search={search}
                filter={filter.resources}
                setFilter={(resources) => setFilter({ ...filter, resources })}
                isOpen={isViewOpen("resources")}
                hasOtherResourceCriteria={hasOtherResourceCriteria}
              />
            )}
            {isActivated("groups") && (
              <ResourceGroups
                search={search}
                filter={filter.groups}
                setFilter={(groups) => setFilter({ ...filter, groups })}
                isOpen={isViewOpen("groups")}
              />
            )}
            {isActivated("projectStatuses") && (
              <ProjectStatuses
                search={search}
                filter={filter.projectStatuses}
                setFilter={(projectStatuses) =>
                  setFilter({ ...filter, projectStatuses })
                }
                isOpen={isViewOpen("projectStatuses")}
              />
            )}
            {isActivated("projectsElementsTags") && (
              <ProjectsElementsTags
                search={search}
                filter={filter.projectsElementsTags}
                setFilter={(projectsElementsTags) =>
                  setFilter({ ...filter, projectsElementsTags })
                }
                isOpen={isViewOpen("projectsElementsTags")}
              />
            )}
            {isActivated("projectsTags") && (
              <ProjectsTags
                search={search}
                filter={filter.projectsTags}
                setFilter={(projectsTags) =>
                  setFilter({ ...filter, projectsTags })
                }
                isOpen={isViewOpen("projectsTags")}
              />
            )}
            {isActivated("resourceTags") && (
              <ResourceTags
                search={search}
                filter={filter.resourceTags}
                setFilter={(resourceTags) =>
                  setFilter({ ...filter, resourceTags })
                }
                isOpen={isViewOpen("resourceTags")}
              />
            )}
            {isActivated("roles") && (
              <Roles
                search={search}
                filter={filter.roles}
                setFilter={(roles) => setFilter({ ...filter, roles })}
                isOpen={isViewOpen("roles")}
                scope={roleScope}
              />
            )}
            {isActivated("organizationalUnits") && (
              <OrganizationalUnits
                search={search}
                filter={filter.organizationalUnits}
                setFilter={(organizationalUnits) =>
                  setFilter({ ...filter, organizationalUnits })
                }
                isOpen={isViewOpen("organizationalUnits")}
              />
            )}
            <div className={styles.eraseButton}>
              <EraseButton
                filter={filter}
                erase={(filter) => {
                  select(!!erase ? erase(filter) : setFilter(filter));
                }}
              />
            </div>
          </div>
        ),
      })}
    </SelectList>
  );
}

export { FilterMenu };
